import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const AdminUsersPanel = withListOverflow(AdminUsersListOwner);
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthError } from "../../data/errors.js";
import { getProvisionedUsers, getSignedInUsers } from "../../data/adminUsersClient.js";
import { T } from "../../app/tokens.js";
import { AdminUserActivityDrawer } from "./AdminUserActivityDrawer.jsx";
import { DirectoryImportModal } from "./DirectoryImportModal.jsx";

const PANEL = {
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  background: T.white,
};

function formatDate(value) {
  if (!value) return "No activity yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Unknown"
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function roleLabel(role) {
  const labels = { admin: "Admin", editor: "Editor", viewer: "Viewer", view_limited: "Viewer" };
  return labels[role] || String(role || "viewer").replaceAll("_", " ");
}

function roleLinkValue(role) {
  return role === "view_limited" ? "viewer" : (role || "viewer");
}

function AdminUsersListOwner({ explainer, currentRole } = {}) {
  const [accountHoldersOnly, setAccountHoldersOnly] = useState(false);
  const [users, setUsers] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [selectedUser, setSelectedUser] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(() => {
    const currentRequest = ++requestId.current;
    setState({ loading: true, error: null });
    const getUsers = accountHoldersOnly ? getSignedInUsers : getProvisionedUsers;
    getUsers()
      .then((data) => {
        if (currentRequest !== requestId.current) return;
        const nextUsers = data.people || [];
        setUsers(nextUsers);
        const requestedId = new URLSearchParams(window.location.search).get("userId");
        if (requestedId) {
          const person = nextUsers.find((user) => user.userId === requestedId);
          setSelectedUser(person ? { ...person, id: person.userId } : null);
        }
        setState({ loading: false, error: null });
      })
      .catch((error) => {
        if (currentRequest === requestId.current) setState({ loading: false, error });
      });
  }, [accountHoldersOnly]);

  useEffect(() => {
    load();
    return () => { requestId.current += 1; };
  }, [load]);

  const userTypeLabel = "people directory";

  const closeDrawer = () => {
    setSelectedUser(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("userId");
    window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <section data-testid="admin-users-panel">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: T.dark, fontSize: T.fsLg5 }}>Users</h2>
      </div>
      {explainer}

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, color: T.dark }}>
        <input type="checkbox" checked={accountHoldersOnly}
          onChange={(event) => setAccountHoldersOnly(event.target.checked)} />
        Account holders only
      </label>

      <p data-testid="admin-users-intended-role-legend" style={{ color: T.mid, fontSize: T.fsXs }}>
        Muted roles show intended access - unconfirmed until first sign-in; signed-in roles are authoritative.
      </p>
      {currentRole === "admin" && <button type="button"
        onClick={() => setImportOpen(true)} data-testid="directory-import-open"
        style={{ marginBottom: 16, padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, color: T.dark, cursor: "pointer" }}>
        Import directory (CSV)
      </button>}
      {!state.loading && !state.error && <p data-testid="directory-people-count" style={{ color: T.mid }}>{users.length} People</p>}
      {state.loading && <div data-testid="admin-users-loading" style={{ color: T.mid, padding: "28px 0" }}>Loading {userTypeLabel}…</div>}
      {state.error && (
        <div data-testid="admin-users-error" role="alert" style={{ ...PANEL, borderColor: T.red, color: T.red, padding: 16 }}>
          {state.error instanceof AuthError
            ? `You are not allowed to view the ${userTypeLabel}.`
            : state.error.message}
          <button type="button" data-testid="admin-users-retry" onClick={load} style={{ marginLeft: 12 }}>Retry</button>
        </div>
      )}
      {!state.loading && !state.error && users.length === 0 && (
        <div data-testid="admin-users-empty" style={{ ...PANEL, color: T.mid, padding: 28 }}>
          {accountHoldersOnly ? "No account holders have been linked to the people directory." : "No people are in the directory yet. An administrator can add people before their first sign-in."}
        </div>
      )}
      {!state.loading && !state.error && users.length > 0 && (
        <div data-testid="admin-users-roster" style={{ ...PANEL, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Name", "Email", "Source", "Organization", "Role", "Okta group(s)", "Status", "Last login", "Last active"].map((label) => (
                    <th key={label} style={{ padding: "10px 14px", textAlign: "left", color: T.mid, background: T.lightGray, fontSize: T.fsXs, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`admin-user-row-${user.id}`}>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}` }}>
                      {user.userId
                        ? <button type="button" data-testid={`admin-user-open-${user.id}`} onClick={() => setSelectedUser({ ...user, id: user.userId })} style={{ border: 0, padding: 0, background: "transparent", color: T.blue, cursor: "pointer", fontWeight: T.fwSemibold }}>{user.displayName || user.email}</button>
                        : <span style={{ color: T.dark }}>{user.displayName || user.email}</span>}
                    </td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.dark }}>{user.email}</td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.mid }}>
                      <span style={{ background: T.lightGray, borderRadius: 4, padding: "2px 6px" }}>{user.source === "okta" ? "Okta" : user.source === "tines" ? "Tines" : "Manual"}</span>
                      {user.isDemo && <span style={{ marginLeft: 6, fontWeight: T.fwSemibold }}>Demo</span>}
                    </td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.mid }}>{user.org || "Not specified"}</td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}` }}>
                       {user.userId ? <a data-testid={`admin-user-role-${user.id}`} href={`/admin?section=roles&role=${encodeURIComponent(roleLinkValue(user.role))}`} style={{ color: T.blue }}>{roleLabel(user.role)}</a>
                         : user.provisionedRole ? <span data-testid={`admin-user-intended-role-${user.id}`} style={{ color: T.mid, background: T.lightGray, borderRadius: 4, padding: "2px 6px" }}>{roleLabel(user.provisionedRole)}</span>
                           : <span style={{ color: T.mid }}>No account</span>}
                    </td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.mid }}>{user.userId ? (user.oktaGroups || []).join(", ") || "None captured" : "Not applicable"}</td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: !user.userId && user.provisionedRole ? T.mid : user.status === "active" ? T.confirmedGreen : T.mid }}>{!user.userId && user.provisionedRole ? `${user.status === "inactive" ? "Inactive - " : ""}Invited - not yet signed in` : user.status}</td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.mid }}>
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never signed in"}
                    </td>
                    <td style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}`, color: T.mid }}>
                      {user.userId ? <a data-testid={`admin-user-activity-${user.id}`} href={`/admin?section=activity&userId=${encodeURIComponent(user.userId)}`} style={{ color: T.blue }}>{formatDate(user.lastActiveAt)}</a> : "Never signed in"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selectedUser && <AdminUserActivityDrawer user={selectedUser} onClose={closeDrawer} />}
      {importOpen && currentRole === "admin" && <DirectoryImportModal onClose={() => setImportOpen(false)} onImported={load} />}
    </section>
  );
}